import { useCallback, useEffect, useRef, useState } from 'react';
import { isRefreshPayloadEqual } from '../utils/criticalResultsRefreshUtils';

export type RefreshTrigger = 'initial' | 'manual' | 'auto' | 'retry';

export interface RefreshFetchResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface UseRefreshSectionOptions<T> {
  enabled?: boolean;
  fetcher: () => Promise<RefreshFetchResult<T>>;
  onWarning?: (message: string, trigger: RefreshTrigger) => void;
}

export function useRefreshSection<T>({
  enabled = true,
  fetcher,
  onWarning,
}: UseRefreshSectionOptions<T>) {
  const [data, setData] = useState<T | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  const dataRef = useRef<T | null>(null);
  const inFlightRef = useRef(false);
  const fetcherRef = useRef(fetcher);
  const onWarningRef = useRef(onWarning);

  useEffect(() => {
    fetcherRef.current = fetcher;
  }, [fetcher]);

  useEffect(() => {
    onWarningRef.current = onWarning;
  }, [onWarning]);

  const refresh = useCallback(
    async (trigger: RefreshTrigger = 'manual') => {
      if (!enabled || inFlightRef.current) {
        return;
      }

      inFlightRef.current = true;
      const isInitial = trigger === 'initial' && dataRef.current == null;
      if (isInitial) {
        setInitialLoading(true);
      } else {
        setRefreshing(true);
      }

      try {
        const result = await fetcherRef.current();
        if (result.success && result.data !== undefined) {
          if (!isRefreshPayloadEqual(dataRef.current, result.data)) {
            dataRef.current = result.data;
            setData(result.data);
          }
          setLastUpdated(new Date());
          setWarning(null);
        } else {
          const message = result.error || 'Unable to refresh data';
          setWarning(message);
          onWarningRef.current?.(message, trigger);
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unable to refresh data';
        setWarning(message);
        onWarningRef.current?.(message, trigger);
      } finally {
        inFlightRef.current = false;
        setInitialLoading(false);
        setRefreshing(false);
      }
    },
    [enabled]
  );

  const reset = useCallback(() => {
    dataRef.current = null;
    setData(null);
    setInitialLoading(true);
    setRefreshing(false);
    setLastUpdated(null);
    setWarning(null);
  }, []);

  return {
    data,
    initialLoading,
    refreshing,
    lastUpdated,
    warning,
    refresh,
    reset,
  };
}

interface UseAutoRefreshSchedulerOptions {
  enabled: boolean;
  intervalMs: number;
  retryDelayMs: number;
  onTick: (trigger: 'auto' | 'retry') => void;
  pauseWhenHidden?: boolean;
}

export function useAutoRefreshScheduler({
  enabled,
  intervalMs,
  retryDelayMs,
  onTick,
  pauseWhenHidden = true,
}: UseAutoRefreshSchedulerOptions) {
  const onTickRef = useRef(onTick);
  const retryTimerRef = useRef<number | null>(null);
  const intervalTimerRef = useRef<number | null>(null);
  const isVisibleRef = useRef(
    typeof document === 'undefined' ? true : document.visibilityState === 'visible'
  );

  useEffect(() => {
    onTickRef.current = onTick;
  }, [onTick]);

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current != null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const scheduleRetry = useCallback(() => {
    if (!enabled) {
      return;
    }
    clearRetryTimer();
    retryTimerRef.current = window.setTimeout(() => {
      if (!pauseWhenHidden || isVisibleRef.current) {
        onTickRef.current('retry');
      }
    }, retryDelayMs);
  }, [clearRetryTimer, enabled, pauseWhenHidden, retryDelayMs]);

  const clearIntervalTimer = useCallback(() => {
    if (intervalTimerRef.current != null) {
      window.clearInterval(intervalTimerRef.current);
      intervalTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    clearIntervalTimer();
    if (!enabled || intervalMs <= 0) {
      return undefined;
    }

    intervalTimerRef.current = window.setInterval(() => {
      if (pauseWhenHidden && !isVisibleRef.current) {
        return;
      }
      onTickRef.current('auto');
    }, intervalMs);

    return clearIntervalTimer;
  }, [clearIntervalTimer, enabled, intervalMs, pauseWhenHidden]);

  useEffect(() => {
    if (!pauseWhenHidden || typeof document === 'undefined') {
      return undefined;
    }

    const handleVisibilityChange = () => {
      isVisibleRef.current = document.visibilityState === 'visible';
      if (isVisibleRef.current && enabled) {
        onTickRef.current('auto');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [enabled, pauseWhenHidden]);

  useEffect(() => clearRetryTimer, [clearRetryTimer]);

  return { scheduleRetry, clearRetryTimer };
}
